/**
 * =============================================================================
 * Página: /afiliado/comissoes
 * Versão: 2.0.0 (Redesign Premium Mobile-First)
 *
 * Descrição:
 *   Extrato premium de comissões. Cards de resumo com gradiente sutil,
 *   pílulas de filtro por programa e mini-grid de detalhamento.
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import { Wallet, Tag, Building2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAffiliateCommissions,
  type AffiliateCommissionRow,
  type CommissionStatus,
} from '@/hooks/useAffiliateCommissions';
import { formatCurrency } from '@/lib/currency-utils';
import { cn } from '@/lib/utils';
import { AffiliatePageHeader } from '@/components/affiliate/shared/AffiliatePageHeader';
import { KpiInfoTooltip } from '@/components/affiliate/shared/KpiInfoTooltip';
import { AffiliateEmptyState } from '@/components/affiliate/shared/AffiliateEmptyState';

// Status renderizado como "dot colorido + texto preto" (sem fundo/badge),
// alinhado ao padrão da tela de Indicações para um visual editorial e limpo.
const STATUS_META: Record<CommissionStatus, { label: string; dotClass: string }> = {
  // Pendente em âmbar (warning) para diferenciar visualmente de "Aprovada" (azul/primary)
  pending:   { label: 'Pendente',  dotClass: 'bg-[hsl(var(--aff-warning))]' },
  approved:  { label: 'Aprovada',  dotClass: 'bg-[hsl(var(--aff-primary))]' },
  paid:      { label: 'Paga',      dotClass: 'bg-[hsl(var(--aff-success))]' },
  cancelled: { label: 'Cancelada', dotClass: 'bg-[hsl(var(--aff-danger))]' },
  reversed:  { label: 'Estornada', dotClass: 'bg-[hsl(var(--aff-danger))]' },
};

function statusBadge(status: CommissionStatus) {
  const meta = STATUS_META[status] ?? { label: status, dotClass: 'bg-[hsl(var(--aff-muted-foreground))]' };
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--aff-foreground))]">
      <span aria-hidden className={cn('h-2 w-2 rounded-full shrink-0', meta.dotClass)} />
      {meta.label}
    </span>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function summarize(rows: AffiliateCommissionRow[]) {
  let pending = 0;
  let approved = 0;
  let paid = 0;
  for (const r of rows) {
    const v = Number(r.reward_value) || 0;
    if (r.status === 'pending') pending += v;
    else if (r.status === 'approved') approved += v;
    else if (r.status === 'paid') paid += v;
  }
  return { pending, approved, paid };
}

export default function AffiliateCommissions() {
  const { data: commissions, isLoading, isError, refetch } = useAffiliateCommissions();
  const [programFilter, setProgramFilter] = useState<string>('all');

  const programs = useMemo(() => {
    const map = new Map<string, string>();
    (commissions ?? []).forEach((c) => {
      if (c.program_id) map.set(c.program_id, c.program?.name ?? 'Programa');
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [commissions]);

  const showProgramFilter = programs.length > 1;
  const filtered = useMemo(() => {
    if (!commissions) return [];
    if (programFilter === 'all') return commissions;
    return commissions.filter((c) => c.program_id === programFilter);
  }, [commissions, programFilter]);

  const totals = summarize(filtered);

  const totalsByProgram = useMemo(() => {
    return programs.map((p) => {
      const rows = (commissions ?? []).filter((c) => c.program_id === p.id);
      return { program: p, totals: summarize(rows), count: rows.length };
    });
  }, [programs, commissions]);

  return (
    <div className="container mx-auto max-w-6xl p-4 md:p-6 space-y-6 aff-animate-in">
      <AffiliatePageHeader
        icon={<Wallet className="h-5 w-5" />}
        title="Comissões"
        subtitle="Extrato das comissões geradas, aprovadas e pagas."
      />

      {/* KPIs de resumo */}
      {!isLoading && !isError && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SummaryCard
            label="Pendente"
            value={totals.pending}
            tone="warning"
            hint="Comissões geradas a partir de indicações convertidas em negócio ganho que ainda aguardam aprovação. Após aprovadas, passam para o status Aprovado e entram no saldo a receber."
          />
          <SummaryCard
            label="Aprovado"
            value={totals.approved}
            tone="info"
            hint="Comissões já validadas e prontas para pagamento. Compõem o seu saldo disponível e serão incluídas no próximo pagamento programado."
          />
          <SummaryCard
            label="Pago"
            value={totals.paid}
            tone="success"
            highlight
            hint="Comissões já transferidas para você através de um pagamento concluído. Representa o valor histórico total recebido."
          />
        </div>
      )}

      {/* Detalhamento por programa */}
      {!isLoading && !isError && showProgramFilter && (
        <section className="space-y-2" aria-label="Comissões por programa">
          <h2 className="px-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[hsl(var(--aff-muted-foreground))]">
            Por programa
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {totalsByProgram.map(({ program, totals: t, count }) => (
              <div key={program.id} className="aff-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[hsl(var(--aff-primary-soft))] text-[hsl(var(--aff-primary))] shrink-0">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <h3 className="font-semibold text-sm truncate text-[hsl(var(--aff-foreground))]">
                      {program.name}
                    </h3>
                  </div>
                  <span className="text-[11px] text-[hsl(var(--aff-muted-foreground))] tabular-nums">
                    {count} comiss{count === 1 ? 'ão' : 'ões'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <MiniTotal label="Pendente" value={t.pending} />
                  <MiniTotal label="Aprovado" value={t.approved} />
                  <MiniTotal label="Pago" value={t.paid} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Filtro pílulas */}
      {showProgramFilter && (
        <ProgramFilterPills
          programs={programs}
          value={programFilter}
          onChange={setProgramFilter}
          totalCount={commissions?.length ?? 0}
          countByProgram={(id) => (commissions ?? []).filter((c) => c.program_id === id).length}
        />
      )}

      {isLoading && <CommissionsSkeleton />}

      {isError && !isLoading && (
        <div className="aff-card flex flex-col items-center gap-3 px-6 py-10 text-center">
          <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
            Não foi possível carregar suas comissões.
          </p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </div>
      )}

      {!isLoading && !isError && filtered.length === 0 && (
        <AffiliateEmptyState
          icon={<TrendingUp className="h-7 w-7" />}
          title={
            programFilter === 'all'
              ? 'Nenhuma comissão ainda'
              : 'Nenhuma comissão neste programa'
          }
          description="Quando suas indicações forem convertidas, as comissões aparecerão aqui."
        />
      )}

      {!isLoading && !isError && filtered.length > 0 && (
        <>
          {/* Desktop */}
          <div className="aff-card hidden md:block overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[hsl(var(--aff-border))] hover:bg-transparent">
                  <TableHead>Indicação</TableHead>
                  {showProgramFilter && <TableHead>Programa</TableHead>}
                  <TableHead>Campanha</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Gerada em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow
                    key={c.id}
                    className="border-[hsl(var(--aff-border))] hover:bg-[hsl(var(--aff-surface-2))]"
                  >
                    <TableCell className="font-semibold text-[hsl(var(--aff-foreground))]">
                      {c.referral?.lead_name || '—'}
                    </TableCell>
                    {showProgramFilter && (
                      <TableCell>
                        <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--aff-surface-2))] px-2 py-0.5 text-[11px] text-[hsl(var(--aff-muted-foreground))]">
                          <Building2 className="h-3 w-3" />
                          {c.program?.name ?? '—'}
                        </span>
                      </TableCell>
                    )}
                    <TableCell className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                      {c.campaign?.name || '—'}
                    </TableCell>
                    <TableCell>{statusBadge(c.status)}</TableCell>
                    <TableCell className="text-right font-semibold tabular-nums text-[hsl(var(--aff-foreground))]">
                      {c.reward_type === 'points'
                        ? `${c.points_earned} pts`
                        : formatCurrency(Number(c.reward_value))}
                    </TableCell>
                    <TableCell className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                      {formatDate(c.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile */}
          <div className="grid gap-3 md:hidden">
            {filtered.map((c) => (
              <MobileCommissionRow
                key={c.id}
                commission={c}
                showProgram={showProgramFilter}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ProgramFilterPills({
  programs,
  value,
  onChange,
  totalCount,
  countByProgram,
}: {
  programs: Array<{ id: string; name: string }>;
  value: string;
  onChange: (v: string) => void;
  totalCount: number;
  countByProgram: (id: string) => number;
}) {
  const items = [
    { id: 'all', name: 'Todos', count: totalCount },
    ...programs.map((p) => ({ id: p.id, name: p.name, count: countByProgram(p.id) })),
  ];
  return (
    <div className="-mx-1 flex gap-2 overflow-x-auto pb-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {items.map((it) => {
        const active = value === it.id;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onChange(it.id)}
            className={cn(
              'shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all',
              active
                ? 'text-[hsl(var(--aff-primary-foreground))] shadow-[var(--aff-shadow-soft)]'
                : 'bg-[hsl(var(--aff-surface))] text-[hsl(var(--aff-muted-foreground))] border border-[hsl(var(--aff-border))]',
            )}
            style={active ? { background: 'var(--aff-gradient-hero)' } : undefined}
          >
            {it.name}
            <span className="ml-1.5 tabular-nums opacity-80">({it.count})</span>
          </button>
        );
      })}
    </div>
  );
}

function MiniTotal({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl bg-[hsl(var(--aff-surface-2))] px-2.5 py-2">
      <div className="text-[9.5px] uppercase tracking-wide text-[hsl(var(--aff-muted-foreground))] font-semibold">
        {label}
      </div>
      <div className="text-[12.5px] font-bold tabular-nums truncate text-[hsl(var(--aff-foreground))]">
        {formatCurrency(value)}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone,
  highlight,
  hint,
}: {
  label: string;
  value: number;
  tone: 'neutral' | 'info' | 'success' | 'warning';
  highlight?: boolean;
  /** Texto explicativo opcional — exibido em tooltip via "?" ao lado do label. */
  hint?: string;
}) {
  const dotClass = {
    neutral: 'bg-[hsl(var(--aff-muted-foreground))]',
    info: 'bg-[hsl(var(--aff-primary))]',
    success: 'bg-[hsl(var(--aff-success))]',
    warning: 'bg-[hsl(var(--aff-warning))]',
  }[tone];

  return (
    <div
      className={cn(
        'aff-card p-4 space-y-1.5 relative overflow-hidden',
        highlight && 'ring-1 ring-[hsl(var(--aff-success)/0.25)]',
      )}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--aff-muted-foreground))]">
        <span className={cn('h-1.5 w-1.5 rounded-full', dotClass)} />
        {label}
        {hint && <KpiInfoTooltip label={label} hint={hint} />}
      </div>
      <div className="text-[22px] font-bold tabular-nums text-[hsl(var(--aff-foreground))]">
        {formatCurrency(value)}
      </div>
    </div>
  );
}

function MobileCommissionRow({
  commission: c,
  showProgram,
}: {
  commission: AffiliateCommissionRow;
  showProgram: boolean;
}) {
  return (
    <div className="aff-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate text-[hsl(var(--aff-foreground))]">
            {c.referral?.lead_name || 'Indicação'}
          </div>
          {showProgram && c.program?.name && (
            <div className="mt-0.5 flex items-center gap-1 text-[11px] text-[hsl(var(--aff-muted-foreground))]">
              <Building2 className="h-3 w-3" />
              <span className="truncate">{c.program.name}</span>
            </div>
          )}
          {c.campaign?.name && (
            <div className="mt-0.5 flex items-center gap-1 text-[11px] text-[hsl(var(--aff-muted-foreground))]">
              <Tag className="h-3 w-3" />
              <span className="truncate">{c.campaign.name}</span>
            </div>
          )}
        </div>
        {statusBadge(c.status)}
      </div>

      <div className="flex items-center justify-between border-t border-[hsl(var(--aff-border))] pt-3 text-xs">
        <span className="text-[hsl(var(--aff-muted-foreground))]">
          {formatDate(c.created_at)}
        </span>
        <span className="font-bold tabular-nums text-[hsl(var(--aff-foreground))]">
          {c.reward_type === 'points'
            ? `${c.points_earned} pts`
            : formatCurrency(Number(c.reward_value))}
        </span>
      </div>
    </div>
  );
}

function CommissionsSkeleton() {
  return (
    <div className="aff-card p-4 space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-6 w-20" />
        </div>
      ))}
    </div>
  );
}
